// Shapes returned by the Hasura actions getRefundOptions / refundOrder
// (ronin-api/controller/refundOrder/engine.js).

export type RefundType = "void" | "full" | "line_item";

export interface RefundOptionItem {
  key: string;
  item_id: string | number;
  name: string;
  price: number;
  modifier_ids: number[];
  modifiers: string;
  unit_discount: number;
  unit_net: number;
  unit_tax: number;
  unit_refund: number;
  // Units paid with a token group apart: no money comes back, the token returns to the band.
  paid_with_token?: boolean;
  token_name?: string | null;
  refundable: number;
  total: number;
}

export interface RefundRecord {
  id: number;
  refund_type: RefundType | "external";
  processor_action: "void" | "return" | "balance" | "cash";
  amount: number;
  subtotal_part: number;
  tax_part: number;
  tip_part: number;
  digital_surcharge_part: number;
  items: { name?: string; unit_refund?: number; modifiers?: unknown }[];
  reason_code: string | null;
  reason: string | null;
  user_id: number | null;
  created_at: string;
}

export interface RefundReason {
  code: string;
  label: string;
  suggests: RefundType | null;
}

export interface RefundLeg {
  transaction_id: number;
  payment_id: number;
  tender: string;
  amount: number;
  processor: string;
  can_void: boolean;
  settled: boolean | null;
  status_before: string;
}

export interface RefundOptions {
  order_id: number;
  status: string;
  processor: string;
  tender: string | null;
  settled: boolean | null;
  status_before: string;
  can_void: boolean;
  available_actions: RefundType[];
  money_outcome: string;
  // The sentence for a line-item refund (a referenced return, never a void).
  money_outcome_line_item?: string | null;
  approved_amount: number;
  refunded_amount: number;
  remaining_amount: number;
  legs: RefundLeg[];
  items: RefundOptionItem[];
  tip: number;
  tip_refundable: boolean;
  digital_surcharge: number;
  digital_surcharge_refundable: boolean;
  refunds: RefundRecord[];
  reasons: RefundReason[];
}

export interface RefundOrderResult {
  statusMessage: string;
  order_refund_id: number | null;
  processor_action: string | null;
  amount: number | null;
  status: string | null;
  money_outcome: string | null;
  replayed: boolean | null;
}

// Fallback when the action has not answered (same list as the API).
export const DEFAULT_REFUND_REASONS: RefundReason[] = [
  { code: "duplicate_charge", label: "Duplicate charge", suggests: "void" },
  { code: "rang_up_by_mistake", label: "Rang up by mistake", suggests: "void" },
  { code: "test_sale", label: "Test sale", suggests: "void" },
  { code: "customer_returned", label: "Customer returned the goods", suggests: "full" },
  { code: "quality_problem", label: "Quality problem", suggests: "line_item" },
  { code: "other", label: "Other", suggests: null },
];
